import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  activatePickupCode as apiActivatePickupCode,
  fetchPickupCode as apiFetchPickupCode,
  reissuePickupCode as apiReissuePickupCode,
  verifyPickupCode as apiVerifyPickupCode,
} from '../util/api';
import { addMarketplaceEntities } from './marketplaceData.duck';

/**
 * @typedef {object} PerTxStatus
 * @property {'idle'|'loading'|'success'|'error'} state
 * @property {any} [error]
 */

/**
 * @typedef {object} PickupState
 * @property {Record<string, any>} codes              tx UUID → BFF response (or `null` when never fetched)
 * @property {Record<string, PerTxStatus>} fetchStatus
 * @property {Record<string, PerTxStatus>} reissueStatus
 * @property {Record<string, PerTxStatus & { result?: any }>} verifyStatus
 */

/** @type {PickupState} */
const initialState = {
  codes: {},
  fetchStatus: {},
  reissueStatus: {},
  verifyStatus: {},
  activateStatus: {},
};

const idle = { state: 'idle' };
const loading = { state: 'loading' };
const success = result => ({ state: 'success', result });
const errored = error => ({ state: 'error', error });

/** GET /api/transactions/pickup/code: buyer reads their OTP. */
export const fetchPickupCodeThunk = createAsyncThunk(
  'pickup/fetchCode',
  async ({ transactionId }, { rejectWithValue }) => {
    try {
      return { transactionId, data: await apiFetchPickupCode(transactionId) };
    } catch (e) {
      return rejectWithValue({ transactionId, message: e?.message || 'fetch failed' });
    }
  }
);

/** POST /api/transactions/pickup/verify: seller submits the buyer's code. BFF
 * response includes the fresh tx for atomic Redux dispatch. */
export const verifyPickupCodeThunk = createAsyncThunk(
  'pickup/verify',
  async ({ transactionId, code }, { dispatch, rejectWithValue }) => {
    try {
      const data = await apiVerifyPickupCode({ transaction_id: transactionId, code });
      if (data?.status === 'verified' && data?.transaction) {
        dispatch(addMarketplaceEntities(data.transaction));
      }
      return { transactionId, data };
    } catch (e) {
      return rejectWithValue({ transactionId, message: e?.message || 'verify failed' });
    }
  }
);

/** POST /api/transactions/pickup/reissue: buyer mints a fresh OTP. */
export const reissuePickupCodeThunk = createAsyncThunk(
  'pickup/reissue',
  async ({ transactionId }, { rejectWithValue }) => {
    try {
      return { transactionId, data: await apiReissuePickupCode({ transactionId }) };
    } catch (e) {
      return rejectWithValue({ transactionId, message: e?.message || 'reissue failed' });
    }
  }
);

/** POST /api/transactions/pickup/activate: customer-activate-pickup-code +
 * OTP mint atomically. BFF response includes the fresh tx for Redux dispatch. */
export const activatePickupCodeThunk = createAsyncThunk(
  'pickup/activate',
  async ({ transactionId }, { dispatch, rejectWithValue }) => {
    try {
      const txId = transactionId?.uuid || transactionId;
      const data = await apiActivatePickupCode({ transactionId: txId });
      if (data?.transaction) {
        dispatch(addMarketplaceEntities(data.transaction));
      }
      return { transactionId: txId, data };
    } catch (e) {
      return rejectWithValue({ transactionId, message: e?.message || 'activate failed' });
    }
  }
);

const slice = createSlice({
  name: 'pickup',
  initialState,
  reducers: {
    /** Reset the verify lifecycle for one transaction (e.g. on dialog close). */
    clearVerifyStatus: (state, action) => {
      const { transactionId } = action.payload || {};
      if (transactionId) state.verifyStatus[transactionId] = idle;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchPickupCodeThunk.pending, (state, action) => {
        state.fetchStatus[action.meta.arg.transactionId] = loading;
      })
      .addCase(fetchPickupCodeThunk.fulfilled, (state, action) => {
        const { transactionId, data } = action.payload;
        state.codes[transactionId] = data;
        state.fetchStatus[transactionId] = success();
      })
      .addCase(fetchPickupCodeThunk.rejected, (state, action) => {
        const { transactionId, message } = action.payload || {};
        state.fetchStatus[transactionId || action.meta.arg.transactionId] = errored(message);
      })
      .addCase(verifyPickupCodeThunk.pending, (state, action) => {
        state.verifyStatus[action.meta.arg.transactionId] = loading;
      })
      .addCase(verifyPickupCodeThunk.fulfilled, (state, action) => {
        const { transactionId, data } = action.payload;
        state.verifyStatus[transactionId] = success(data);
      })
      .addCase(verifyPickupCodeThunk.rejected, (state, action) => {
        const { transactionId, message } = action.payload || {};
        state.verifyStatus[transactionId || action.meta.arg.transactionId] = errored(message);
      })
      .addCase(reissuePickupCodeThunk.pending, (state, action) => {
        state.reissueStatus[action.meta.arg.transactionId] = loading;
      })
      .addCase(reissuePickupCodeThunk.fulfilled, (state, action) => {
        const { transactionId, data } = action.payload;
        state.codes[transactionId] = data;
        state.reissueStatus[transactionId] = success();
      })
      .addCase(reissuePickupCodeThunk.rejected, (state, action) => {
        const { transactionId, message } = action.payload || {};
        state.reissueStatus[transactionId || action.meta.arg.transactionId] = errored(message);
      })
      .addCase(activatePickupCodeThunk.pending, (state, action) => {
        const txId = action.meta.arg.transactionId?.uuid || action.meta.arg.transactionId;
        state.activateStatus[txId] = loading;
      })
      .addCase(activatePickupCodeThunk.fulfilled, (state, action) => {
        const { transactionId, data } = action.payload;
        state.activateStatus[transactionId] = success(data);
        if (data?.code) {
          state.codes[transactionId] = data.code;
          state.fetchStatus[transactionId] = success();
        }
      })
      .addCase(activatePickupCodeThunk.rejected, (state, action) => {
        const { transactionId, message } = action.payload || {};
        const txId = transactionId?.uuid || transactionId || action.meta.arg.transactionId?.uuid || action.meta.arg.transactionId;
        state.activateStatus[txId] = errored(message);
      });
  },
});

export const { clearVerifyStatus } = slice.actions;

const pickStatus = (s) => {
  const status = s || idle;
  return {
    isLoading: status.state === 'loading',
    isSuccess: status.state === 'success',
    isError: status.state === 'error',
    error: status.error || null,
    result: status.result,
  };
};

/** Buyer-facing hook. Returns `{ data, isLoading, error, refetch }`. */
export const usePickupCode = transactionId => {
  const dispatch = useDispatch();
  const data = useSelector(state => (transactionId ? state.pickup.codes[transactionId] : null));
  const status = useSelector(state =>
    transactionId ? state.pickup.fetchStatus[transactionId] : null
  );
  const refetch = useCallback(() => {
    if (transactionId) dispatch(fetchPickupCodeThunk({ transactionId }));
  }, [dispatch, transactionId]);
  const view = pickStatus(status);
  return { data, refetch, ...view };
};

/** Seller-facing verify hook. Returns `{ verify, data, isLoading, error, reset }`. */
export const useVerifyPickup = transactionId => {
  const dispatch = useDispatch();
  const status = useSelector(state =>
    transactionId ? state.pickup.verifyStatus[transactionId] : null
  );
  const verify = useCallback(
    code => dispatch(verifyPickupCodeThunk({ transactionId, code })),
    [dispatch, transactionId]
  );
  const reset = useCallback(
    () => dispatch(clearVerifyStatus({ transactionId })),
    [dispatch, transactionId]
  );
  const view = pickStatus(status);
  return { verify, data: view.result, ...view, reset };
};

/** Buyer-facing reissue hook. Returns `{ reissue, isLoading, error }`. */
export const useReissuePickup = transactionId => {
  const dispatch = useDispatch();
  const status = useSelector(state =>
    transactionId ? state.pickup.reissueStatus[transactionId] : null
  );
  const reissue = useCallback(
    () => dispatch(reissuePickupCodeThunk({ transactionId })),
    [dispatch, transactionId]
  );
  return { reissue, ...pickStatus(status) };
};

/** Buyer-facing activate hook. Fires privileged transition + mints OTP atomically via BFF. */
export const useActivatePickup = transactionId => {
  const dispatch = useDispatch();
  const txId = transactionId?.uuid || transactionId;
  const status = useSelector(state => (txId ? state.pickup.activateStatus[txId] : null));
  const activate = useCallback(
    () => dispatch(activatePickupCodeThunk({ transactionId })),
    [dispatch, transactionId]
  );
  return { activate, ...pickStatus(status) };
};

export default slice.reducer;
